import axios from 'axios';

// ─── Base URL ─────────────────────────────────────────────────────────────────
// In development points to your droplet, in production same domain
const BASE_URL = process.env.REACT_APP_API_URL || 'http://159.223.213.92:3000';

// ─── Axios Instance ───────────────────────────────────────────────────────────
const api = axios.create({
  baseURL: BASE_URL,
});

// ─── Auth Interceptor ─────────────────────────────────────────────────────────
// Automatically attaches token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ledger_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ─── Response Interceptor ─────────────────────────────────────────────────────
// Automatically logs out if token expires
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('ledger_token');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const login = (email, password) =>
  api.post('/api/auth/login', { email, password });

export const signup = (name, email, password) =>
  api.post('/api/auth/signup', { name, email, password });

export const getMe = () =>
  api.get('/api/auth/me');

export const updateMode = (app_mode) =>
  api.patch('/api/auth/mode', { app_mode });

// ─── Budget ───────────────────────────────────────────────────────────────────
export const getCategories = () =>
  api.get('/api/budget/categories');

export const addCategory = (data) =>
  api.post('/api/budget/categories', data);

export const updateCategory = (id, data) =>
  api.patch(`/api/budget/categories/${id}`, data);

export const deleteCategory = (id) =>
  api.delete(`/api/budget/categories/${id}`);

export const getBudgetSummary = () =>
  api.get('/api/budget/summary');

export const getBudgetPeriod = () =>
  api.get('/api/budget/period');

// ─── Transactions ─────────────────────────────────────────────────────────────
export const getTransactions = (filters = {}) =>
  api.get('/api/transactions', { params: filters });

export const addTransaction = (data) =>
  api.post('/api/transactions', data);

export const updateTransaction = (id, data) =>
  api.patch(`/api/transactions/${id}`, data);

export const deleteTransaction = (id) =>
  api.delete(`/api/transactions/${id}`);

export const setTransactionSplits = (id, splits) =>
  api.put(`/api/transactions/${id}/splits`, { splits });

// Clear the manual lock so auto-categorization can touch this transaction again.
export const unlockTransaction = (id) =>
  api.post(`/api/transactions/${id}/unlock`);

// Flatten split transactions for client-side category math. A split parent is
// replaced by one synthetic row per slice (carrying the slice's category_id +
// amount but the parent's date/type), so any code that sums transactions by
// category counts the splits instead of the whole parent. Unsplit txns pass
// through unchanged. Mirrors the server-side transaction_category_amounts view.
export const expandSplits = (transactions = []) => {
  const out = [];
  for (const t of transactions) {
    if (t.is_split && Array.isArray(t.splits) && t.splits.length) {
      for (const s of t.splits) out.push({ ...t, category_id: s.category_id, amount: s.amount });
    } else {
      out.push(t);
    }
  }
  return out;
};

// ─── Income ───────────────────────────────────────────────────────────────────
export const getIncome = () =>
  api.get('/api/income');

export const addIncome = (data) =>
  api.post('/api/income', data);

export const updateIncome = (id, data) =>
  api.patch(`/api/income/${id}`, data);

export const deleteIncome = (id) =>
  api.delete(`/api/income/${id}`);

// ─── Accounts ─────────────────────────────────────────────────────────────────
export const getAccounts = () =>
  api.get('/api/accounts');

export const getBalances = () =>
  api.get('/api/accounts/balances');

export const updateAccount = (id, data) => api.patch(`/api/accounts/${id}`, data);

// ─── Household ────────────────────────────────────────────────────────────────
export const getHousehold = () =>
  api.get('/api/household');

export const updateHousehold = (data) =>
  api.patch('/api/household', data);

export const inviteMember = (email) =>
  api.post('/api/household/invite', { email });



// ─── Plaid ────────────────────────────────────────────────────────────────────
export const createLinkToken = () =>
  api.post('/api/plaid/create-link-token');

export const exchangeToken = (public_token) =>
  api.post('/api/plaid/exchange-token', { public_token });

export const syncPlaid = (force = true) =>
  api.post(`/api/plaid/sync?force=${force}`);

export default api;

export const getPlaidMappings   = ()     => api.get('/api/plaid-mappings');
export const updatePlaidMapping = (data) => api.put('/api/plaid-mappings', data);

export const getPlaidBanks = () => api.get('/api/plaid/banks');
export const disconnectBank = (itemId) => api.delete(`/api/plaid/disconnect/${itemId}`);
export const createUpdateLinkToken = (itemId) => api.post('/api/plaid/create-update-link-token', { item_id: itemId });
export const getSavingsAdvisor  = () => api.get('/api/budget/savings-advisor');
export const getMonthlyAdvisor  = () => api.get('/api/budget/monthly-advisor');
export const getPriorities      = () => api.get('/api/budget/priorities');
export const savePriorities     = (priorities) => api.patch('/api/budget/priorities', { priorities });
export const advisorChat        = (messages) => api.post('/api/budget/advisor-chat', { messages });
// ─── Google Sheets Sync ───────────────────────────────────────────────────────
export const syncGoogleSheets = (force = true) =>
  api.post(`/api/sync/google?force=${force}`);

export const getGoogleSyncStatus = () =>
  api.get('/api/sync/google/status');

// ─── Savings & Debt ───────────────────────────────────────────────────────────
export const getSavingsGoals  = ()       => api.get('/api/savings/goals');
export const createSavingsGoal = (data)  => api.post('/api/savings/goals', data);
export const updateSavingsGoal = (id, data) => api.patch(`/api/savings/goals/${id}`, data);
export const deleteSavingsGoal = (id)    => api.delete(`/api/savings/goals/${id}`);

export const getDebts         = ()       => api.get('/api/savings/debts');
export const createDebt       = (data)   => api.post('/api/savings/debts', data);
export const updateDebt       = (id, data) => api.patch(`/api/savings/debts/${id}`, data);
export const deleteDebt       = (id)     => api.delete(`/api/savings/debts/${id}`);

export const getFinancialFocus  = ()     => api.get('/api/savings/focus');
export const updateFinancialFocus = (data) => api.patch('/api/savings/focus', data);

// ─── Scenarios (Analytical planning) ────────────────────────────────────────────
export const getScenarios          = ()            => api.get('/api/scenarios');
export const createScenario        = (data)        => api.post('/api/scenarios', data);
export const getScenario           = (id)          => api.get(`/api/scenarios/${id}`);
export const updateScenario        = (id, data)    => api.patch(`/api/scenarios/${id}`, data);
export const deleteScenario        = (id)          => api.delete(`/api/scenarios/${id}`);
export const addScenarioItem       = (id, data)    => api.post(`/api/scenarios/${id}/items`, data);
export const updateScenarioItem    = (id, itemId, data) => api.patch(`/api/scenarios/${id}/items/${itemId}`, data);
export const deleteScenarioItem    = (id, itemId)  => api.delete(`/api/scenarios/${id}/items/${itemId}`);
export const getScenarioProjection = (id, params)  => api.get(`/api/scenarios/${id}/projection`, { params });

// ─── Monthly Deep Dive (Forecast stage 4b) ──────────────────────────────────────
// `params` = { month: 'YYYY-MM', today: 'YYYY-MM-DD' } — same today-forwarding
// pattern as getScenarioProjection, since the server runs on UTC.
export const getDeepDive       = (params) => api.get('/api/budget/deep-dive', { params });
export const getDeepDiveReview = (params) => api.get('/api/budget/deep-dive-review', { params });

// Type-aware budget health (Dashboard's "on pace" headline) — same today-forwarding
// pattern, `params` = { today: 'YYYY-MM-DD' }.
export const getBudgetHealth   = (params) => api.get('/api/budget/health', { params });

// ─── Trends (analytical mode) ───────────────────────────────────────────────────
// `params` = { periods: 8, month: 'YYYY-MM', today: 'YYYY-MM-DD' } — all optional.
export const getTrends         = (params) => api.get('/api/budget/trends', { params });

// ─── Year Overview (analytical mode) ────────────────────────────────────────────
// `params` = { year: 2026, today: 'YYYY-MM-DD' } — year optional (defaults server-side).
export const getYearOverview   = (params) => api.get('/api/budget/year-overview', { params });
